import type { PlanEntitlement } from '../types/planTypes';

/**
 * publicChapterPolicy - 公开章节访问策略（文档 §5.3）
 *
 * 解析 PlanEntitlement 的 publicTextAccess / publicMediaAccess 字段，
 * 提供纯函数供 buildChapterAccess 和 accessPolicyService 使用。
 *
 * FREE_CHAPTER_COUNT = 3（文档 §7.3）
 * - metered: 第 1-3 章全文，第 4+ 章 25% 摘要
 * - full: 所有章节全文
 * - free_chapters: 第 1-3 章媒体，第 4+ 章无媒体
 * - all: 所有章节媒体
 * - none: 无媒体
 */

/** 前 N 章免费（文档 §7.3 硬编码 3） */
export const FREE_CHAPTER_COUNT = 3;

/** 解析正文的访问模式。 */
export function resolveTextMode(
	entitlement: Pick<PlanEntitlement, 'publicTextAccess'>,
): 'full' | 'metered' {
	return entitlement.publicTextAccess === 'full' ? 'full' : 'metered';
}

/** 解析媒体的访问模式。 */
export function resolveMediaAccess(
	entitlement: Pick<PlanEntitlement, 'publicMediaAccess'>,
): 'none' | 'free_chapters' | 'all' {
	const access = entitlement.publicMediaAccess;
	if (access === 'all') return 'all';
	if (access === 'none') return 'none';
	return 'free_chapters';
}

/**
 * 判断指定章节是否可全文阅读
 * @param chapterIndex 1-based 章节序号
 * @param entitlement 观看者的权益快照
 * @returns true = 全文，false = 25% 摘要
 */
export function canReadFullText(
	chapterIndex: number,
	entitlement: Pick<PlanEntitlement, 'publicTextAccess'>,
): boolean {
	const mode = resolveTextMode(entitlement);
	if (mode === 'full') return true;
	// metered: 第 1-3 章全文
	return chapterIndex <= FREE_CHAPTER_COUNT;
}

/**
 * 判断指定章节是否可播放媒体
 * @param chapterIndex 1-based 章节序号
 * @param entitlement 观看者的权益快照
 * @returns true = 可播放，false = 锁定
 */
export function canPlayMedia(
	chapterIndex: number,
	entitlement: Pick<PlanEntitlement, 'publicMediaAccess'>,
): boolean {
	const access = resolveMediaAccess(entitlement);
	if (access === 'all') return true;
	if (access === 'none') return false;
	// free_chapters: 第 1-3 章可播放
	return chapterIndex <= FREE_CHAPTER_COUNT;
}

/**
 * 计算 partial 正文的预览比例
 * metered 模式下第 4+ 章返回 0.25
 */
export function resolvePreviewRatio(
	chapterIndex: number,
	entitlement: Pick<PlanEntitlement, 'publicTextAccess'>,
): number {
	if (canReadFullText(chapterIndex, entitlement)) return 1;
	return 0.25;
}
